/**
 * AI Mode Spectrum — the unified, ordered posture spectrum.
 *
 * The desktop replaced the old off/advisor/autonomous triad with ONE ordered
 * spectrum. The desktop `set_global_ai_mode` handler normalizes BOTH the new
 * spectrum values AND the legacy values, so sending the spectrum strings is
 * always safe and back-compatible.
 *
 *   dormant   (was 'off')        — asleep. never watches, never acts.
 *   observing (NEW)              — always watching + narrating, never inflicts.
 *   advising  (was 'advisor')    — proposes actions for your approval.
 *   acting    (was 'autonomous') — acts autonomously, in character.
 */

export const AI_SPECTRUM = ['dormant', 'observing', 'advising', 'acting'];

export const SPECTRUM_META = {
    dormant: {
        id: 'dormant',
        label: 'Dormant',
        color: '#9ca3af',
        rank: 0,
        desc: 'Asleep. The AI does not watch, suggest, or act. Manual control only.',
    },
    observing: {
        id: 'observing',
        label: 'Observing',
        color: '#3b82f6',
        rank: 1,
        desc: 'Always watching and narrating. It forms intentions but never inflicts anything — a live read on what it would do.',
    },
    advising: {
        id: 'advising',
        label: 'Advising',
        color: '#f59e0b',
        rank: 2,
        desc: 'Proposes actions for your approval. Nothing runs without your say-so.',
    },
    acting: {
        id: 'acting',
        label: 'Acting',
        color: '#16a34a',
        rank: 3,
        desc: 'Acts autonomously, in character, toward your guiding goals — no approval needed.',
    },
};

/** Ordered list of {id, label, color, rank, desc} for rendering selectors. */
export const SPECTRUM_LIST = AI_SPECTRUM.map((id) => SPECTRUM_META[id]);

/** Legacy persisted/transport value → spectrum id. */
const LEGACY_TO_SPECTRUM = {
    off: 'dormant',
    advisor: 'advising',
    autonomous: 'acting',
};

/**
 * Map a chat-feature mode to each spectrum posture. The chat feature is a
 * separate transport from the global mode; observing keeps the chat in 'human'
 * (the AI watches via the policy engine but does not drive the conversation).
 * These are the DESKTOP chat-manager's names (human / augmented / ai).
 */
export const SPECTRUM_TO_CHAT_MODE = {
    dormant: 'human',
    observing: 'human',
    advising: 'augmented',
    acting: 'ai',
};

/**
 * Map a spectrum posture to the Chat view's legacy off/advisor/autonomous
 * vocabulary. Chat.jsx still branches on those three names internally; this is
 * the SINGLE source for that projection (the view used to keep its own copy).
 * `observing` behaves like `off` for the conversation surface (the AI watches
 * but does not drive chat) — the view surfaces a distinct "watching" indicator
 * so the operator can still tell Observing from Dormant.
 */
export const SPECTRUM_TO_LEGACY_CHAT = {
    dormant: 'off',
    observing: 'off',
    advising: 'advisor',
    acting: 'autonomous',
};

/**
 * Normalize any persisted/legacy value to a valid spectrum id. Accepts spectrum
 * ids (pass-through), legacy ids ('off'/'advisor'/'autonomous'), or junk
 * (defaults to 'dormant'). Use this when migrating localStorage 'aiMode'.
 */
export function normalizeToSpectrum(value) {
    if (!value) return 'dormant';
    if (AI_SPECTRUM.includes(value)) return value;
    return LEGACY_TO_SPECTRUM[value] || 'dormant';
}

/** True when the posture lets the AI inflict actions (advising queues, acting runs). */
export function isInflictingMode(spectrum) {
    return spectrum === 'advising' || spectrum === 'acting';
}

/** True when the AI is at least watching (everything except dormant). */
export function isAwake(spectrum) {
    return spectrum !== 'dormant';
}
